import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, DatePicker, InputNumber, Table, Tag, Space, Alert, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const HOLD_REASONS = ['customer_reserve', 'quality_issue', 'regulatory', 'damage', 'recall', 'other'];

// inventoryService / holdService 由不同 caller 提供（InventoriesList 等），
// 各家 service 對 getRecords / create 的 return type 細節不一致；modal 內部僅以 unknown 處理 response，
// 故宣告為純 index signature 以接受任何 caller 形狀，避免「Two different types with this name exist」錯誤。
interface InventoryService {
  [key: string]: unknown;
}

interface HoldService {
  [key: string]: unknown;
}

interface InventoryRecord {
  id: string;
  product_sku?: string;
  product_name?: string;
  inventory_type?: string;
  batch?: string | null;
  expiration_date?: string | null;
  available_quantity?: number;
  [key: string]: unknown;
}

interface InventoryItem extends InventoryRecord {
  hold_quantity: number;
}

interface BatchInventoryHoldModalProps {
  visible: boolean;
  selectedIds: Set<string>;
  inventoryService: InventoryService;
  holdService: HoldService;
  merchantId?: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

/**
 * 庫存列表批次保留 Modal
 * 從庫存列表勾選多筆庫存 → 彈出此 Modal → 設定原因/數量 → 建立一張 InventoryHold
 */
const BatchInventoryHoldModal: React.FC<BatchInventoryHoldModalProps> = ({
  visible,
  selectedIds,
  inventoryService,
  holdService,
  merchantId,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingInventories, setFetchingInventories] = useState(false);
  const [items, setItems] = useState<InventoryItem[]>([]);

  // 開啟時用 IDs 查詢庫存詳情
  useEffect(() => {
    if (visible && selectedIds && selectedIds.size > 0) {
      form.resetFields();
      fetchInventories();
    } else {
      setItems([]);
    }
  }, [visible, selectedIds]);

  const fetchInventories = async () => {
    setFetchingInventories(true);
    try {
      const params = {
        'inventories.id': Array.from(selectedIds),
        'inventories.status': ['in_stock'],
        page_size: selectedIds.size,
      };
      const response = await (inventoryService.getRecords as (params: Record<string, unknown>) => Promise<{ success: boolean; data?: InventoryRecord[]; message?: string }>)(params);
      if (response.success) {
        const data = response.data || [];
        setItems(data.map((inv: InventoryRecord) => ({
          ...inv,
          hold_quantity: ((inv.available_quantity ?? 0) > 0) ? (inv.available_quantity as number) : 0,
        })));
      } else {
        message.error(response.message || t('common.message.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch inventories:', error);
      message.error(t('common.message.fetch_failed'));
    } finally {
      setFetchingInventories(false);
    }
  };

  const handleQuantityChange = (inventoryId: string, value: number | null) => {
    setItems(prev => prev.map(item =>
      item.id === inventoryId ? { ...item, hold_quantity: (value ?? 0) } : item
    ));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const validItems = items.filter(item => item.hold_quantity > 0);
      if (validItems.length === 0) {
        message.error(t('shared.inventory_holds.error.no_items'));
        return;
      }

      setLoading(true);
      const payload: Record<string, unknown> = {
        reason: values.reason,
        note: values.note,
        expires_on: values.expires_on ? values.expires_on.format('YYYY-MM-DD') : null,
        items: validItems.map(item => ({
          inventory_id: item.id,
          quantity: item.hold_quantity,
        })),
      };
      if (merchantId) payload.merchant_id = merchantId;

      const response = await (holdService.create as (payload: Record<string, unknown>) => Promise<{ success: boolean; message?: string }>)(payload);
      if (response.success) {
        message.success(t('common.message.create_success'));
        onSuccess?.();
        onCancel();
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error: any) {
      // antd Form.validateFields 失敗會帶 errorFields，欄位本身已標紅，不需 toast
      if (error?.errorFields) return;
      console.error('[BatchInventoryHoldModal] submit failed:', error);
      message.error(t('common.message.operation_failed'));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('models.inventory_hold.product'),
      key: 'product',
      render: (_: unknown, r: InventoryItem) => (
        <>
          <div style={{ fontWeight: 500 }}>{r.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.product_name}</div>
        </>
      ),
    },
    {
      title: t('models.inventory_type._'),
      key: 'inventory_type',
      width: 100,
      render: (_: unknown, r: InventoryItem) => r.inventory_type || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      width: 100,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 110,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.available_quantity'),
      key: 'available',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, r: InventoryItem) => <Tag color={(r.available_quantity ?? 0) > 0 ? 'green' : 'red'}>{r.available_quantity}</Tag>,
    },
    {
      title: t('shared.inventory_holds.hold_quantity'),
      key: 'hold_quantity',
      width: 150,
      render: (_: unknown, r: InventoryItem) => {
        const disabled = !r.available_quantity || r.available_quantity <= 0;
        return (
          <Space size={4}>
            <InputNumber
              min={0}
              max={r.available_quantity || 0}
              value={disabled ? 0 : r.hold_quantity}
              onChange={(val) => handleQuantityChange(r.id, val as number | null)}
              size="small"
              style={{ width: 90 }}
              disabled={disabled}
            />
            <span style={{ fontSize: 12, color: '#999' }}>/ {r.available_quantity || 0}</span>
          </Space>
        );
      },
    },
  ];

  const totalHoldQty = items.reduce((sum, item) => sum + (item.hold_quantity || 0), 0);
  const validCount = items.filter(item => item.hold_quantity > 0).length;

  return (
    <Modal
      title={
        <span>
          <LockOutlined style={{ color: '#faad14', marginRight: 8 }} />
          {t('shared.inventory_holds.batch_hold_title')}
        </span>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={t('common.button.submit')}
      cancelText={t('common.button.cancel')}
      okButtonProps={{ disabled: totalHoldQty === 0 }}
      width={900}
      destroyOnClose
    >
      <Form form={form} layout="inline" style={{ marginBottom: 16, flexWrap: 'wrap', gap: '8px 0' }} initialValues={{ reason: 'customer_reserve' }}>
        <Form.Item
          label={t('models.inventory_hold.reason')}
          name="reason"
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <Select style={{ width: 160 }}>
            {HOLD_REASONS.map(reason => (
              <Select.Option key={reason} value={reason}>
                {t(`enums.inventory_hold.reason.${reason}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label={t('models.inventory_hold.expires_on')} name="expires_on">
          <DatePicker
            placeholder={t('common.placeholder.select_date')}
            disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
          />
        </Form.Item>
        <Form.Item label={t('models.inventory_hold.note')} name="note" style={{ flex: 1, minWidth: 150 }}>
          <Input placeholder={t('common.placeholder.note')} />
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={fetchingInventories}
        pagination={false}
        size="small"
        scroll={{ y: 350 }}
        rowClassName={(r) => (!r.available_quantity || r.available_quantity <= 0) ? 'ant-table-row-disabled' : ''}
      />

      {validCount > 0 && (
        <Alert
          message={t('shared.inventory_holds.batch_hold_summary', { count: validCount, total: totalHoldQty })}
          type="info"
          showIcon
          style={{ marginTop: 12 }}
        />
      )}
    </Modal>
  );
}

export default BatchInventoryHoldModal;
